/**
 * routes.config.js — SOURCE DE VERITE UNIQUE des routes applicatives.
 *
 * Centralise, pour chaque page :
 *   - le chemin (path)                    -> consomme par routeBuilder + router
 *   - les roles autorises (roles)         -> consomme par RouteGuard + Menu
 *   - la presence et le placement au menu (menu) -> consomme par le Menu
 *   - le composant lazy-load (component) via bascule V2/V3 (features.flags)
 *
 * AJOUTER UNE PAGE = AJOUTER UNE ENTREE ICI. Rien d'autre a toucher
 * (ni le router, ni le menu, ni un guard).
 *
 * ----------------------------------------------------------------------------
 * AJOUTER UNE PAGE DANS LES PARAMETRES (panneau /settings) :
 *
 *   1. Importer l'icone MUI en haut de ce fichier.
 *   2. Ajouter une entree dans la section // ─── SETTINGS ─── ci-dessous :
 *
 *      {
 *        id: 'ma-nouvelle-page',               // identifiant unique kebab-case
 *        path: 'settings/ma-nouvelle-page',    // URL : /settings/ma-nouvelle-page
 *        roles: ALL_PAGE_ACCESS_ROLES,         // roles autorises
 *        menu: {
 *          show: true,
 *          section: 'settings',
 *          label: 'Mon label',                 // texte affiché dans le panneau
 *          icon: MonIconeOutlinedIcon,          // icone importee a l'etape 1
 *          beta: true,                          // optionnel : affiche le badge "BETA" dans le menu
 *          new: true,                           // optionnel : affiche le badge "new" dans le menu
 *        },
 *        component: lazy(() => import('client/v3/pages/MaPage/MaPage')),
 *      },
 *
 *   Le menu Parametres (Menu.js) se met a jour automatiquement.
 *   Aucune autre modification necessaire.
 *
 * ----------------------------------------------------------------------------
 * VERSIONNAGE V2/V3 :
 * Tant qu'une page n'a pas de version V3, on importe directement son fichier V2
 * existant. Des qu'une V3 existe, basculer l'entree sur `pickVersion(FLAGS.X, v3, v2)`
 * (voir l'entree `organisation` comme reference).
 *
 * SOUS-ROUTES (children) :
 * Une entree peut declarer des `children`. Chaque enfant devient une route
 * soeur `parent/enfant` (et non imbriquee). Sans `component`, l'enfant reutilise
 * celui du parent ; il peut surcharger `roles` pour un acces plus restreint.
 * ----------------------------------------------------------------------------
 */

import { lazy } from 'react';
import { ROLES, ALL_PAGE_ACCESS_ROLES, ROLE_WITH_MENU_ACCESS} from './roles';
import { FLAGS, pickVersion } from './features.flags';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AppSettingsAltOutlinedIcon from '@mui/icons-material/AppSettingsAltOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';


/**
 * ROLE_LANDING_MAP — Page d'atterrissage apres login par role.
 *
 * AJOUTER UN ROLE = ajouter une entree ici (et une route dans ROUTE_CONFIG si besoin).
 * Ordre important : le premier match gagne.
 */
export const ROLE_LANDING_MAP = [
  { roles: ['mobiuspack-admin', 'account-manager', 'project-manager'], path: '/my-network' },
  { roles: ['store-manager'], path: '/store-manager' },
  { roles: ['group-manager'], path: '/my-region' },
  { roles: ['warehouse-manager'], path: '/my-warehouse' },
  { roles: ['prospect'], path: '/default' },
  { roles: ['partner'], path: '/partner' },
];

/**
 * MENU_SECTIONS — vue d'ensemble des groupes du menu principal (consommee par Menu.js).
 * Chaque route rejoint un groupe via menu.section (voir ROUTE_CONFIG plus bas).
 *
 *   label  : texte de l'en-tete de groupe (null si affiche a plat, sans en-tete)
 *   icon   : icone de l'en-tete de groupe (null si pas de groupe)
 *
 * Le comportement plie/deplie de chaque groupe (ex: Espace admin, Vue par profil) est code en dur
 * dans Menu.js (state local + <Collapse>, propre a chaque section) — pas pilote depuis cet objet.
 *
 * NB: 'network' et 'profile-view' changent de forme selon le role de l'utilisateur
 * (groupe pour un role a large acces, liste a plat sans en-tete pour un manager) —
 * cette bascule reste geree dans Menu.js (inNetworkGroup / inProfileViewGroup), pas ici.
 */
export const MENU_SECTIONS = {
  main: {
    label: null,
    icon: null,
  },
  network: {
    label: 'Mon réseau',
    icon: HubOutlinedIcon,
  },
  'profile-view': {
    label: 'Vue par profil',
    icon: GridViewOutlinedIcon,
  },
  admin: {
    label: 'Espace admin',
    icon: AdminPanelSettingsOutlinedIcon,
  },
  partner: {
    label: null,
    icon: null,
  },
  settings: {
    label: 'Paramètres',
    icon: null,
  },
};

export const ROUTE_CONFIG = [
  // ─── CLIENT ────────────────────────────────────────────────────────────
  {
    id: 'store-manager',
    path: 'store-manager',
    roles: [ROLES.STORE_MANAGER.id, ROLES.ADMIN.id],
    menu: { show: true, section: 'profile-view', label: 'Mon magasin' },
    component: lazy(() => import('client/v3/pages/StoreManager/StoreManager')),
  },
  {
    id: 'my-region',
    path: 'my-region',
    roles: [ROLES.GROUP_MANAGER.id, ROLES.ADMIN.id],
    menu: { show: true, section: 'profile-view', label: 'Ma région' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_MY_REGION,
        () => import('client/v3/pages/MyRegion/MyRegion'),
        () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
      )(),
    ),
  },
  {
    id: 'my-warehouse',
    path: 'my-warehouse',
    roles: [ROLES.WAREHOUSE_MANAGER.id, ROLES.ADMIN.id],
    menu: { show: true, section: 'profile-view', label: 'Mon entrepôt' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_MY_WAREHOUSE,
        () => import('client/v3/pages/WarehouseManager/WarehouseManager'),
        () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
      )(),
    ),
  },
  // Page decisionnel retiree du menu et non migree en V3 : redirige vers l'accueil
  // pour ne plus taper les endpoints v2 depreciés.
  { path: 'decision', redirect: '' },

  {
    id: 'my-orders',
    path: 'my-orders',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: true, section: 'main', label: 'Mes commandes', beta: true },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_MY_ORDERS,
        () => import('client/v3/pages/MyOrders/MyOrders'),
        () => import('client/v2/pages/MyOrders/MyOrders'),
      )(),
    ),
  },
  {
    id: 'compliance',
    path: 'compliance',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: true, section: 'main', label: 'Conformité ESG' },
    component: lazy(() => import('client/v2/pages/UnderConstruction/UnderConstruction')),
  },
  {
    id: 'my-network',
    path: 'my-network',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: true, section: 'network', label: 'Mon réseau' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_MY_NETWORK,
        () => import('client/v3/pages/MyNetwork/MyNetwork'),
        () => import('client/v2/pages/Decision/Decision'),
      )(),
    ),
  },
  {
    id: 'view-by-location',
    path: 'view-by-location',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: true, section: 'network', label: 'Par lieu' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_VIEW_BY_LOCATION,
        () => import('client/v3/pages/ViewByLocation/ViewByLocation'),
        () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
      )(),
    ),
    children: [
      {
        path: 'upstream',
        component: lazy(() =>
          pickVersion(
            FLAGS.USE_V3_VIEW_BY_LOCATION,
            () => import('client/v3/pages/ViewByLocation/ViewByLocationAmont'),
            () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
          )(),
        ),
      },
      {
        path: 'downstream',
        component: lazy(() =>
          pickVersion(
            FLAGS.USE_V3_VIEW_BY_LOCATION,
            () => import('client/v3/pages/ViewByLocation/ViewByLocationAval'),
            () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
          )(),
        ),
      },
    ],
  },
  {
    id: 'view-by-flow',
    path: 'view-by-flow',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: true, section: 'network', label: 'Par flux' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_VIEW_BY_FLOW,
        () => import('client/v3/pages/ViewByFlow/ViewByFlow'),
        () => import('client/v2/pages/UnderConstruction/UnderConstruction'),
      )(),
    ),
  },
  // Page operationnel retiree du menu et jamais migree en V3 : redirige vers l'accueil
  // pour ne plus taper les endpoints v2 depreciés.
  { path: 'operation', redirect: '' },
  {
    id: 'historic',
    path: 'historic',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id, ROLES.GROUP_MANAGER.id, ROLES.WAREHOUSE_MANAGER.id, ROLES.STORE_MANAGER.id],
    menu: { show: true, section: 'network', label: 'Historique' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_HISTORIC,
        () => import('client/v3/pages/Historic/Historic'),
        () => import('client/v2/pages/Historic/Historic'),
      )(),
    ),
    children: [
      { path: 'export' },
      { path: 'import' },
    ],
  },
  // ─── SETTINGS ──────────────────────────────────────────────────────────────
  // Toutes les pages paramètres vivent sous /settings/*.
  // Ajouter une route ici (menu.show + section:'settings' + menu.icon) = apparaît automatiquement dans le menu.
  // Alias legacy : garde les anciens liens/favoris vers settings/organisation valides ( 30/07,
  // renommage settings/organization).
  { path: 'settings/organisation', redirect: 'settings/organization' },
  {
    id: 'organisation',
    path: 'settings/organization',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Organisation', icon: ApartmentOutlinedIcon },
    page: { title: 'Organisation' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'members',
    path: 'settings/members',
    roles: [ROLES.ADMIN.id, ROLES.ACCOUNT_MANAGER.id, ROLES.GROUP_MANAGER.id, ROLES.PROJECT_MANAGER.id, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Membres', icon: GroupOutlinedIcon },
    component: lazy(() => import('client/v3/pages/Members/Members.page')),
  },
  {
    id: 'flow',
    path: 'settings/flow',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Mes flux', icon: AccountTreeOutlinedIcon },
    page: { title: 'Mes flux' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'location',
    path: 'settings/location',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Mes lieux', icon: RoomOutlinedIcon },
    page: { title: 'Mes lieux', tabGroup: 'location', tabLabel: 'Lieux' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'location-groups',
    path: 'settings/location-groups',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: false },
    page: { title: 'Groupes de lieux', tabGroup: 'location', tabLabel: 'Groupes de lieux' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'carrier',
    path: 'settings/carrier',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'settings', label: 'Transporteurs', icon: LocalShippingOutlinedIcon },
    page: { title: 'Transporteurs' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'mobiuscan-connection',
    path: 'settings/mobiuscan-connection',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Config MOBIUScan', icon: AppSettingsAltOutlinedIcon },
    page: { title: 'Connexion', tabGroup: 'action', tabLabel: 'Connexion' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'action',
    path: 'settings/action',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: false },
    page: { title: 'Config MOBIUScan', tabGroup: 'action', tabLabel: 'Actions' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'maintenance',
    path: 'settings/maintenance',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Catégorisation des anomalies', icon: CategoryOutlinedIcon },
    page: { title: 'Catégorisation des anomalies' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'disposable-packaging',
    path: 'settings/disposable-packaging',
    roles: ALL_PAGE_ACCESS_ROLES,
    menu: { show: FLAGS.USE_V3_ORGANISATION, section: 'settings', label: 'Consommation en jetable', icon: RecyclingOutlinedIcon },
    page: { title: 'Consommation en jetable' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_ORGANISATION,
        () => import('client/v3/pages/Organisation/Organisation'),
        () => import('client/v2/pages/Organisation/Organisation'),
      )(),
    ),
  },
  {
    id: 'action-edit',
    path: 'settings/action/edit',
    roles: [ROLES.ADMIN.id],
    menu: { show: false },
    component: lazy(() => import('client/v2/pages/EditAction/EditActionPage')),
  },
  // Alias : garde l'ancien lien /settings/integration (sans sous-page) valide,
  // pointe vers l'onglet par défaut du groupe (Webhooks).
  { path: 'settings/integration', redirect: 'settings/integration/webhooks' },
  {
    id: 'integration',
    path: 'settings/integration/webhooks',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: true, section: 'settings', label: 'Intégration', icon: ExtensionOutlinedIcon },
    page: { title: 'Intégration', tabGroup: 'integration', tabLabel: 'Webhooks' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_INTEGRATION,
        () => import('client/v3/pages/Integration/Integration'),
        () => import('client/v2/pages/Integration/Integration'),
      )(),
    ),
  },
  {
    id: 'integration-api-key',
    path: 'settings/integration/api-key',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: false },
    page: { title: 'Intégration', tabGroup: 'integration', tabLabel: 'Clés API' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_INTEGRATION,
        () => import('client/v3/pages/Integration/Integration'),
        () => import('client/v2/pages/Integration/Integration'),
      )(),
    ),
  },
  {
    id: 'integration-documentation',
    path: 'settings/integration/documentation',
    roles: [...ALL_PAGE_ACCESS_ROLES, ROLES.PARTNER.id],
    menu: { show: false },
    page: { title: 'Intégration', tabGroup: 'integration', tabLabel: 'Documentation technique' },
    component: lazy(() =>
      pickVersion(
        FLAGS.USE_V3_INTEGRATION,
        () => import('client/v3/pages/Integration/Integration'),
        () => import('client/v2/pages/Integration/Integration'),
      )(),
    ),
  },

  // ─── PARTNER ───────────────────────────────────────────────────────────
  {
    id: 'industrial',
    path: 'partner/industrial',
    roles: [ROLES.PARTNER.id, ROLES.ADMIN.id],
    menu: { show: true, section: 'partner', label: 'Industriel' },
    component: lazy(() => import('partner/v2/pages/Industrial/Industrial')),
  },

  // ─── ADMIN ─────────────────────────────────────────────────────────────
  {
    id: 'admin-carrier-perf',
    path: 'admin/carrier-perf',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'admin', label: 'Performance transporteurs' },
    component: lazy(() => import('admin/v2/pages/CarrierPerf/CarrierPerfPage')),
  },
  {
    id: 'admin-reconditioning',
    path: 'admin/reconditioning',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'admin', label: 'Reconditionnement' },
    component: lazy(() => import('client/v3/pages/Reconditioning/Reconditioning')),
  },
  {
    id: 'admin-map',
    path: 'admin/map',
    roles: [ROLES.ADMIN.id],
    menu: { show: false, section: 'admin', label: 'Cartographie reverse' },
    component: lazy(() => import('admin/v2/pages/Map/MapPage')),
  },
  {
    id: 'admin-exceptions',
    path: 'admin/exceptions',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'admin', label: 'Monitoring des erreurs de scans' },
    component: lazy(() => import('admin/v2/pages/Exceptions/ExceptionsPage')),
  },
  {
    id: 'admin-mail-supervision',
    path: 'admin/mail-supervision',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'admin', label: 'Gestion des emails conso' },
    component: lazy(() => import('admin/v2/pages/MailSupervision/MailSupervision')),
  },
  {
    id: 'admin-qrcode',
    path: 'admin/qrcode',
    roles: [ROLES.ADMIN.id],
    menu: { show: true, section: 'admin', label: 'Générateur de QR Codes' },
    component: lazy(() => import('admin/v2/pages/Qrcode/QrcodeGenerator')),
  },


  // ─── ROUTES APPLICATIVES (dans Root, authentifiées) ────────────────────
  {
    id: 'auth-landing',
    path: '',
    roles: ROLE_WITH_MENU_ACCESS, // accessible a tous les connectes : redirige selon ROLE_LANDING_MAP
    menu: { show: false },
    component: lazy(() => import('services/security/Auth')),
  },
  {
    id: 'profile',
    path: 'settings/profile',
    roles: ROLE_WITH_MENU_ACCESS,
    menu: { show: true, section: 'settings', label: 'Mon compte', icon: PersonOutlineOutlinedIcon },
    component: lazy(() =>
      import('client/v2/pages/Profile/Profile').then((m) => ({ default: m.Profile })),
    ),
  },
  // Page notifications retiree  : redirige vers l'accueil.
  { path: 'notifications', redirect: '' },

  // ─── ROUTES PUBLIQUES (hors Root, sans ProtectedRoute) ─────────────────
  {
    id: 'auth',
    path: 'auth',
    public: true,
    component: lazy(() => import('services/security/Auth')),
  },
  {
    id: 'access-denied',
    path: 'access-denied',
    public: true,
    component: lazy(() => import('client/v2/pages/AccessDenied/AccessDenied')),
  },
  {
    id: 'email-verification',
    path: 'email-verification',
    public: true,
    component: lazy(() => import('global/v2/pages/EmailNotVerified/EmailNotVerified')),
  },
  {
    id: 'default',
    path: 'default',
    public: true,
    component: lazy(() => import('global/v2/pages/Default/Default')),
  },
];
